// src/components/desk/PropertyTimeline.jsx — the B20 timeline spine, shared.
// ----------------------------------------------------------------------------
// evLabel / evTime / burst-collapsing / keyset paging were extracted VERBATIM
// from ManualPropertyModal.jsx (they lived there since the §3 decomposition)
// so the LINEAGE modal can render the same stream. What is shared is the
// PROJECTION — verb→sentence, burst collapsing, paging. The CHROME is not:
// ManualPropertyModal wraps it in a <Facet>, the lineage modal in a full-width
// band. One renderer, two containers (the CooperationChips rule).
//
// CLUSTER, NOT PROPERTY (2026-07-27). The manual modal always looks at one
// property row, but a portal property is routinely SEVERAL property rows joined
// retroactively by same_as identity verdicts — the four postings of აკაკი
// წერეთლის გამზირი 82 are four `property` rows, linked hours after the last one
// appeared. Keyed on a single property_id this timeline would show a quarter of
// the story and silently call it complete, so the hook takes an ID LIST and the
// server takes a comma-separated :id. A single id still works unchanged.
//
// The label dictionary keeps its original no-hiding contract: an unknown verb
// renders as the raw verb (`· verb`), never as nothing. Labels compress for
// cognition; the SEQUENCE is never editorialized.

import { useEffect, useMemo, useState } from 'react'
import { t, tLog } from '../../lib/i18n.js'
import { sourceKindLabel } from '../../lib/labels.js'
import { Icon } from '../Icon.jsx'
import { btn } from './primitives.jsx'
import { getPropertyEvents } from '../../db/core.js'
import { fmtTbilisi } from '../../lib/tbilisiTime.js'
import { now } from '../../lib/clock.js'
import { stabilizeEvents } from '../../lib/stableChanges.js'

export function evLabel(e, listings) {
  const d = e.data || {}
  const src = e.subject_type === 'listing' ? listings?.[e.subject_id]?.source : null
  const v = e.verb
  if (v === 'property.created') return t('desk_timeline_property_registered')
  if (v === 'property.updated') return t('desk_timeline_details_updated')
  if (v === 'property.context_signal') return tLog('desk_timeline_context_signal', { kind: d.kind ? `: ${d.kind}` : '' })
  // sourceKindLabel, not the raw value (2026-07-24): this printed "— scrape" / "— agent" in
  // the agent-facing timeline, which is the raw-enum leak the display dictionary exists to stop.
  if (v === 'claim.committed') return tLog('desk_timeline_observed', { attribute: d.attribute || 'a fact', source: d.source_kind ? ` — ${sourceKindLabel(d.source_kind)}` : '' })
  // desk_timeline_feature's template carries a stray "{" (absent)" if false}" segment from the
  // source translation doc (not a real {placeholder} — no word-only token to substitute), so the
  // absent-flag suffix can't be localized through it; only {key} is wired here.
  if (v === 'property.feature') return tLog('desk_timeline_feature', { key: d.key || '?' })
  if (v === 'listing.upserted') return tLog('desk_timeline_listing_observed', { src: src ? ` on ${src}` : '', price: d.price != null ? ` — ${d.price} ${d.currency || ''}` : '' })
  if (v === 'listing.state') return tLog('desk_timeline_listing_kind', { kind: d.kind || 'state', value: String(d.value ?? '') })
  if (v === 'listing.changed') {
    // ⚠️ AUTHOR-EVIDENCE DIFFS WITH A NULL SIDE ARE ARTIFACTS, NOT OBSERVATIONS
    // (2026-07-28). Until migration 0239 two diverged observe_listing bodies were
    // live: the re-check crawler ran the older one, which omits the author columns,
    // so every crawler observation wrote author_phone/name/party NULL — and the next
    // full scrape diffed a real phone against that NULL. Measured: 670 "appeared",
    // 1,241 "vanished", 339 phantom names, against 217 genuine changes. 73% of the
    // author-evidence history is invented.
    //
    // 0239 stopped new ones; these are the survivors. They are NOT deleted — the
    // event log is append-only (0206) and they are honest records of what the system
    // believed — they are simply not RENDERED as if a phone had changed. A one-sided
    // author diff now means "the last look didn't capture it", which is silence, and
    // silence is not a change (PHILOSOPHY: unknown is not "no").
    // Genuine changes (both sides present) still render.
    const AUTHOR_EVIDENCE = new Set(['author', 'author_phone', 'author_name'])
    const parts = Object.entries(d)
      .filter(([, x]) => x && typeof x === 'object' && 'from' in x)
      .filter(([k, x]) => !(AUTHOR_EVIDENCE.has(k) && (x.from == null || x.to == null)))
    if (!parts.length) return null
    // T34: `pending` is stamped by stabilizeEvents on a value that has not yet held for the
    // stability window. It is DRAWN, never hidden — an agent who just saved and comes to look
    // must see their change acknowledged, or the history reads as a save that failed.
    const pending = parts.some(([, x]) => x.pending)
    const mark = pending ? t('desk_timeline_change_pending') : ''
    if (parts.length === 1) {
      const [k, x] = parts[0]
      return tLog('desk_timeline_listing_changed', { field: k, from: x.from, to: x.to }) + (src ? ` (${src})` : '') + mark
    }
    const joined = parts.map(([k, x]) => `${k} ${x.from} → ${x.to}`).join(', ')
    return `Δ Listing changed${joined ? ': ' + joined : ''}${src ? ` (${src})` : ''}${mark}`
  }
  if (v === 'listing.reshare_declared') return tLog('desk_timeline_reshare_declared', { src: src ? ` (${src})` : '' })
  if (v === 'listing.reshare_registered') return tLog('desk_timeline_reshare_registered', { src: src ? ` (${src})` : '' })
  if (v === 'listing_terms.amended') {
    const ops = (d.ops || []).map(o => `${o.op} ${o.field}`).join(', ')
    return tLog('desk_timeline_terms_amended', { ops })
  }
  if (v === 'listing_terms.ungoverned_write') return t('desk_timeline_terms_ungoverned')
  if (v.startsWith('cooperation.')) return tLog('desk_timeline_agency_agreement', { verb: v.split('.')[1].replace(/_/g, ' '), status: d.status || '' })
  if (v === 'contact_link.declared') return tLog('desk_timeline_contact_declared', { role: d.role ? ` (${d.role})` : '', name: d.evidence?.raw?.name ? `: ${d.evidence.raw.name}` : '' })
  if (v === 'contact_link.dismissed') return t('desk_timeline_contact_dismissed')
  return `· ${v}`
}

// TBILISI, never the device (owner ruling 2026-08-12, extended to this timeline on
// 2026-08-15). This was `toLocaleString([])` — the DEVICE's zone — so the same observation
// read at a different hour depending on who opened it, and across midnight on a different
// DAY. That matters more here than almost anywhere else in the Desk: this column is what an
// agent reasons about ORDER with ("the owner reposted before the price moved"), and two
// people comparing screens were comparing two clocks.
//
// ⚠️ ONE formatter for the whole timeline — LineageModal's episode rows and its raw-event
// drawer both import this, which is why it was extracted. Fixing it here fixes every
// consumer.
// Burst collapsing is deliberately untouched: it groups on ADJACENCY, not on calendar days,
// so no grouping decision depends on which zone a midnight falls in.
export const evTime = ts => fmtTbilisi(ts, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })

// A scrape commits dozens of claims/features in one burst — collapse runs of
// ≥4 so one observation session reads as one row, expandable to the raw facts.
export function collapseBursts(events) {
  const evs = events || []
  const isBurstVerb = e => e.verb === 'claim.committed' || e.verb === 'property.feature'
  const out = []
  let i = 0
  while (i < evs.length) {
    if (isBurstVerb(evs[i])) {
      let j = i
      while (j < evs.length && isBurstVerb(evs[j])) j++
      if (j - i >= 4) { out.push({ burst: evs.slice(i, j) }); i = j; continue }
    }
    out.push({ e: evs[i] }); i++
  }
  return out
}

// propertyIds: a single id or an identity CLUSTER's ids. Sorted + joined so a
// re-render with the same set in a different order doesn't refetch (the lineage
// modal builds its id list from siblings, whose order is not guaranteed).
export function usePropertyEvents(propertyIds, refreshKey, { limit = 100 } = {}) {
  const key = useMemo(() => {
    const arr = Array.isArray(propertyIds) ? propertyIds : [propertyIds]
    return [...new Set(arr.filter(Boolean))].sort().join(',')
  }, [propertyIds])

  const [feed, setFeed] = useState(null)     // { events, has_more, listings }
  const [loadingOlder, setLoadingOlder] = useState(false)
  const [err, setErr] = useState(null)

  useEffect(() => {
    if (!key) { setFeed(null); return }
    let gone = false
    setFeed(null)
    getPropertyEvents(key, { limit })
      .then(d => { if (!gone) { setFeed(d); setErr(null) } })
      .catch(e => { if (!gone) setErr(e.message) })
    return () => { gone = true }
  }, [key, refreshKey, limit])

  async function loadOlder() {
    if (!feed?.events?.length || loadingOlder) return
    setLoadingOlder(true)
    try {
      const d = await getPropertyEvents(key, { limit, before: feed.events[feed.events.length - 1].id })
      setFeed(f => ({ ...d, events: [...f.events, ...d.events], listings: { ...f.listings, ...d.listings } }))
    } catch (e) { setErr(e.message) }
    finally { setLoadingOlder(false) }
  }

  return { feed, err, loadingOlder, loadOlder }
}

// The list itself — no section chrome, so each host supplies its own container.
export function TimelineBody({ feed, err, loadingOlder, loadOlder }) {
  // T34 (owner ruling 2026-09-02): a change exists when a value SETTLES, not when someone
  // saves — so the typing is removed BEFORE burst collapsing, which groups on adjacency and
  // would otherwise count a typo and its correction as two members of a burst.
  // ⚠️ clock.now(), never Date.now(): the window is measured against the present, and a device
  // clock running fast would mark every edit settled and silently switch the rule off.
  // ⚠️ Computed over the LOADED events only, which is correct rather than approximate: an
  // edit's stability depends on the NEXT edit to that field, and pages arrive newest-first, so
  // whenever an edit is loaded every edit that could settle it is loaded too.
  const rows = useMemo(() => collapseBursts(stabilizeEvents(feed?.events, now().getTime())), [feed])
  return (
    <>
      {err && <p className="text-sm text-destructive"><Icon name="alert-triangle" /> {err}</p>}
      {!feed && !err && <p className="text-xs text-muted-foreground">{t('desk_loading')}</p>}
      {feed?.events?.length === 0 && <p className="text-xs text-muted-foreground">{t('desk_timeline_no_events')}</p>}
      <ul className="text-xs text-muted-foreground space-y-1">
        {rows.map(r => r.burst ? (
          <li key={r.burst[0].id}>
            <details>
              <summary className="cursor-pointer">
                {/* same raw-enum fix as claim.committed above */}
                {tLog('desk_timeline_burst_summary', { evTime: evTime(r.burst[0].occurred_at), n: r.burst.length, sourceKind: r.burst[0].data?.source_kind ? ` — ${sourceKindLabel(r.burst[0].data.source_kind)}` : '' })}
              </summary>
              <ul className="pl-4 mt-1 space-y-0.5">
                {/* evLabel returns null for an event that is pure artifact (a
                    one-sided author diff — see the 0239 note above). Dropping the
                    ROW, not just the text, or the list renders empty bullets. */}
                {r.burst.map(e => ({ e, label: evLabel(e, feed.listings) }))
                  .filter(x => x.label != null)
                  .map(({ e, label }) => <li key={e.id}>{label}</li>)}
              </ul>
            </details>
          </li>
        ) : evLabel(r.e, feed.listings) == null ? null : (
          <li key={r.e.id}>
            <span className="tabular-nums">{evTime(r.e.occurred_at)}</span> — {evLabel(r.e, feed.listings)}
          </li>
        ))}
      </ul>
      {feed?.has_more && (
        <button className={`${btn('ghost')} mt-2`} disabled={loadingOlder} onClick={loadOlder}>
          {loadingOlder ? t('desk_timeline_loading') : t('desk_timeline_load_older')}
        </button>
      )}
    </>
  )
}
